import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowForward, LocalShipping } from "@mui/icons-material";
import { FlagsSap, PoComponent } from "../../../../models/general/po-component";
import { URLS } from "../../../../services/app-urls";
import { BaseService } from "../../../../services/base-service";
import { translate } from "../../../../translate/translate";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";

interface UpcomingDeliveriesResponse {
  results: PoComponent[];
}

interface StatusPresentation {
  color: string;
  label: string;
}

const MOCK_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const getStatusPresentation = (delivery: PoComponent): StatusPresentation => {
  if (delivery.sap_flags === FlagsSap.PAST_DATE_ERROR) {
    return { color: "#ef4444", label: translate.t("expired_date") };
  }

  if (delivery.invoice) {
    return { color: "#3b82f6", label: translate.t("material_in_transit") };
  }

  return { color: "#f59e0b", label: translate.t("waiting_invoice") };
};

const formatDeliveryDate = (value?: Date): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(translate.language === "en" ? "en-US" : "pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const UpcomingDeliveriesComponent = () => {
  const service = useMemo(() => new BaseService<UpcomingDeliveriesResponse>(URLS.PO_COMPONENT), []);
  const [deliveries, setDeliveries] = useState<PoComponent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    service.clearParameters();
    service.addParameter("limit", 4);
    service
      .getFromListRoute("upcoming-deliveries")
      .then(response => setDeliveries(response.data.results ?? []))
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [service]);

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        fontFamily: MOCK_FONT_FAMILY,
        height: "100%",
        overflow: "hidden",
        width: "100%",
        "& .MuiAlert-message, & .MuiButton-root, & .MuiTableCell-root, & .MuiTypography-root": {
          fontFamily: MOCK_FONT_FAMILY,
        },
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          borderBottom: "1px solid #eef0f3",
          display: "flex",
          justifyContent: "space-between",
          px: "20px",
          pb: "12px",
          pt: "16px",
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
          <LocalShipping color="primary" sx={{ fontSize: 15 }} />
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 600 }}>
            {translate.t("upcoming_deliveries")}
          </Typography>
        </Box>
        <Button
          component={NavLink}
          to="/general/po"
          endIcon={<ArrowForward sx={{ fontSize: "11px !important" }} />}
          size="small"
          sx={{ fontSize: 12, fontWeight: 400, textTransform: "none" }}
        >
          {translate.t("see_all")}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 180 }}>
          <CircularProgress aria-label={translate.t("loading")} size={28} />
        </Box>
      ) : hasError ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {translate.t("upcoming_deliveries_error")}
        </Alert>
      ) : deliveries.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 7, textAlign: "center" }}>
          {translate.t("no_upcoming_deliveries")}
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
          <Table
            aria-label={translate.t("upcoming_deliveries")}
            size="small"
            sx={{
              cursor: "default",
              width: "100%",
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #f9fafb",
                fontSize: "12px !important",
                padding: "10px 12px",
              },
              "& .MuiTableCell-root:first-of-type": { paddingLeft: "16px", paddingRight: "16px" },
            }}
          >
            <TableHead
              sx={{
                "& .MuiTableRow-head": { backgroundColor: "#f9fafc !important" },
                "& .MuiTableCell-head": {
                  color: "#45485f !important",
                  fontSize: "11px !important",
                  fontWeight: "600 !important",
                },
              }}
            >
              <TableRow>
                <TableCell>{translate.t("part_number")}</TableCell>
                <TableCell>{translate.t("description")}</TableCell>
                <TableCell>{translate.t("po")}</TableCell>
                <TableCell>{translate.t("invoice")}</TableCell>
                <TableCell align="right">{translate.t("quantity")}</TableCell>
                <TableCell>{translate.language === "en" ? "Arrival" : "Chegada"}</TableCell>
                <TableCell>{translate.t("status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map((delivery, index) => {
                const status = getStatusPresentation(delivery);
                return (
                  <TableRow
                    key={delivery.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "rgba(249, 250, 251, 0.3)",
                      transition: "background-color 150ms ease",
                      "&:hover": { backgroundColor: "rgba(249, 250, 251, 0.6)" },
                    }}
                  >
                    <TableCell sx={{ color: "#1f2937", fontWeight: 500, whiteSpace: "nowrap" }} title={delivery.part_number ?? undefined}>{delivery.part_number ?? "-"}</TableCell>
                    <TableCell sx={{ color: "#6b7280", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={delivery.description ?? undefined}>
                      {delivery.description ?? "-"}
                    </TableCell>
                    <TableCell sx={{ color: "#4b5563", whiteSpace: "nowrap" }} title={delivery.purchase_order_number ?? undefined}>{delivery.purchase_order_number ?? "-"}</TableCell>
                    <TableCell sx={{ color: "#4b5563", whiteSpace: "nowrap" }} title={delivery.invoice ?? undefined}>{delivery.invoice ?? "-"}</TableCell>
                    <TableCell align="right" sx={{ color: "#374151", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{parseLocaleNumber(Number(delivery.quantity))}</TableCell>
                    <TableCell sx={{ color: "#4b5563", whiteSpace: "nowrap" }}>{formatDeliveryDate(delivery.estimated_time_delivery)}</TableCell>
                    <TableCell>
                      <Box sx={{ alignItems: "center", color: status.color, display: "flex", gap: 0.5, minWidth: 0 }} title={status.label}>
                        <Box sx={{ backgroundColor: status.color, borderRadius: "50%", flex: "0 0 auto", height: 7, width: 7 }} />
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {status.label}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default UpcomingDeliveriesComponent;
